import { GameState, NavigationArrow } from "@shared/types";
import { GameEvent } from "../enums/GameEvent";
import { GameRouteService } from "../services/GameRouteService";
import { GameEventService, UpdateNavigationArrowsEvent } from "../services/GameEventService";
import { css, html, htmlArray } from "../helpers/webComponents";

/** CSS affecting the {@link NavigationComponent} */
const styles: string = css`
    .arrows {
        position: relative;
        width: 100%;
        height: 100%;
    }

    .arrow {
        position: absolute;
        width: 7%;
        height: auto;
        pointer-events: auto;
        cursor: var(--cursor-hover);
        opacity: 75%;
        filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.1));
        /* mix-blend-mode: lighten; Helps blend into the background */
        transition: transform 0.2s ease, opacity 0.2s ease, filter 0.1s ease-in-out;
        animation:  glow 2s infinite ease-in-out;
        padding: 15px;
    }
    /* @keyframes subtle-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
} */

@keyframes glow {
  0%, 100% {
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
  }
  50% {
    filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.9));
  }
}


    .arrow.hover:hover {
        opacity: 100%;
    }

    .right {
        padding: 15px 0 15px 15px;
    }
    .down {
        padding: 15px 15px 0 15px;
    }
    .left {
        padding: 15px 15px 15px 0;
    }

    .right.hover:hover {
        transform: scale(1.2) translateX(12%);
    }
    .down.hover:hover {
        transform: scale(1.2) translateY(12%);
    }
    .left.hover:hover {
        transform: scale(1.2) translateX(-12%);
    }

    .arrow:active {
        transform: scale(1);
        filter: drop-shadow(0 0 10px rgba(255, 255, 255, 1));
        cursor: var(--cursor-active);
    }

    /* Tooltip Styles */
    .tooltip {
        visibility: hidden;
        background-color: rgba(0, 0, 0, 0.7);
        color: #fff;
        text-align: center;
        border-radius: 5px;
        padding: 5px;
        position: absolute;
        z-index: 1;
        bottom: 75%; /* Position above the arrow */
        left: 50%;
        transform: translateX(-50%);
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
    }
    .arrow-container.hover:hover .tooltip {
        visibility: visible;
        opacity: 1;
    }
`;

/**
 * A Room Arrow Generator Web Component
 *
 * @remarks Will listen for the Update Navigation Arrows event and render the room arrows if possible
 */
export class NavigationComponent extends HTMLElement {
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Listen for the Update Navigation Arrows event
        this._gameEventService.addGameEventListener<UpdateNavigationArrowsEvent>(GameEvent.UpdateNavigationArrows, event => {
            this.render(event.arrows);
        });
    }

    /**
     * Render the contents of <navigation-arrows> - the arrows which are used in a room
     *
     * @param arrows Arrow array to render
     */
    private render(arrows: NavigationArrow[]): void {
        if (this.shadowRoot) {
            const elements: HTMLElement[] = htmlArray`
                    <style>
                        ${styles}
                    </style>
                    <div class="arrows">
                        ${arrows.map(arrow => this.renderNavigationArrow(arrow))}
                    </div>                
                `;

            this.shadowRoot.append(...elements);
        }
    }

    /**
     * Render the arrow element
     *
     * @param arrow Arrow to render
     *
     * @returns String with raw HTML for the arrow element
     */
    private renderNavigationArrow(arrow: NavigationArrow): HTMLElement {
        const element: HTMLElement = html`
            <div class="arrow-container">
                <img class="arrow ${arrow.arrowDirection}" style="${this.generatePosition(arrow)}" src="/assets/img/rooms/layers/${arrow.arrowDirection}-arrow.png"/>
                <span class="tooltip">${arrow.arrowText}</span> <!-- Custom Tooltip -->
            </div>
        `;

        element.addEventListener("mouseenter", () => this.playSound("button-hover.mp3"));
        element.addEventListener("click", () => this.handleClickArrow(arrow));

        setTimeout(() => {
            element.querySelector("img")?.classList.add("hover");
            element.classList.add("hover");
        }, 400);
        return element;
    }

    /**
     * Generate css style for arrow element to position it correctly in the room
     *
     * @param arrow Arrow to generate the position for
     *
     * @returns String with css properties to position the arrow correctly
     */
    private generatePosition(arrow: NavigationArrow): string {
        let x: number = arrow.arrowPosition.x;
        let y: number = arrow.arrowPosition.y;

        let cssArrowY: string = "";
        let cssArrowX: string = "";

        if (y < 0) {
            cssArrowY = "bottom";
            y = y * -1;
        }
        else {
            cssArrowY = "top";
        }

        if (x < 0) {
            cssArrowX = "left";
            x = x * -1;
        }
        else {
            cssArrowX = "right";
        }

        const arrowStyle: string = `${cssArrowY}:${y}%; ${cssArrowX}:${x}%`;

        return arrowStyle;
    }

    /**
     * Handle the click on an arrow and create an event for CanvasComponent to use to navigate to a room
     *
     * @param arrow Arrow that was clicked
     */
    private async handleClickArrow(arrow: NavigationArrow): Promise<void> {
        this.playSound("button-click.mp3");

        // Execute the navigation and update the game state
        const state: GameState | undefined = await this._gameRouteService.executeRoomNavigation(arrow.roomAlias);

        if (state === undefined) {
            return;
        }

        this._gameEventService.switchRoom(state);
    }

    private playSound(name: string): void {
        this._gameEventService.playSound(name);
    }
}
